<template>
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Engagement List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Engagement
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="engagements"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-info me-2" @click="viewEngagement(item as any)">
                            <i class="fa fa-eye"></i>
                        </button>
                        <button class="btn btn-sm btn-warning me-2" @click="editEngagement(item as any)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteEngagement(item as any)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="engagementModal" tabindex="-1" ref="modalRef" aria-labelledby="engagementModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Code</label>
                                <input v-model="form.code" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                        </div>
                        <hr>
                        <div class="mb-2">
                            <label class="form-label col-form-label">Accounts</label>
                            <div v-for="(information, idx) in form.informations" :key="information.ID ?? idx" class="border rounded p-2 mb-2">
                                <div class="row g-2">
                                    <div class="col-md-4">
                                        <input v-model="information.code" type="text" class="form-control" placeholder="Account Number" required>
                                    </div>
                                    <div class="col-md-4">
                                        <input v-model="information.name" type="text" class="form-control" placeholder="Account Name" required>
                                    </div>
                                    <div class="col-md-1 d-flex align-items-center">
                                        <button type="button" class="btn btn-danger btn-sm" @click="removeAccount(idx)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button type="button" class="btn btn-success btn-sm mt-2" @click="addAccount">
                            <i class="fa fa-plus"></i> Add Account
                        </button>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="saveEngagement">Save</button>
                    </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="viewEngagementModal" tabindex="-1" ref="viewModalRef">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Engagement & Account Information</h5>
                    <button type="button" class="btn-close" @click="closeViewModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="selectedEngagement">
                        <div class="mb-2"><strong>Code:</strong> {{ selectedEngagement.code }}</div>
                        <div class="mb-2"><strong>Engagement:</strong> {{ selectedEngagement.name }}</div>
                        <div>
                            <strong>Accounts:</strong>
                            <ul v-if="selectedEngagement.informations && selectedEngagement.informations.length">
                                <li v-for="(information, idx) in selectedEngagement.informations" :key="information.ID ?? idx">
                                    <span>
                                        <strong>No:</strong> {{ information.code }},
                                        <strong>Nama:</strong> {{ information.name }},
                                    </span>
                                </li>
                            </ul>
                            <div v-else>
                                <em>No informations available.</em>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeViewModal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

const engagements = ref<any[]>([])
const loading = ref(false)
const total = ref(0)
const modalTitle = ref('')
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const headers = [
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Actions', value: 'actions' }
]

const initForm = {
    ID: null,
    code: '',
    name: '',
    informations: [] as {
        ID: number | null,
        code: string,
        name: string,
    }[]
}

interface AccountInformation {
    ID: number | null;
    code: string;
    name: string;
}

const form = reactive({
    ...initForm
})

const dataEngagements: any[] = []
import * as master from "@/api/services/MasterRequest";

const fetchEngagements = async () => {
    loading.value = true
    try {
        const resp = await master.postRequest("/engagement/list", {...params});
        dataEngagements.splice(0, dataEngagements.length);
        dataEngagements.push(...resp.data);

        let filteredEngagements = [...dataEngagements]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredEngagements = dataEngagements.filter(engagement =>
                engagement.code.toLowerCase().includes(searchLower) ||
                engagement.name.toLowerCase().includes(searchLower)
            )
        }

        const start = (params.page - 1) * params.size
        const end = start + params.size
        engagements.value = filteredEngagements.slice(start, end)
        total.value = filteredEngagements.length

    } catch (error) {
        toast.error('Failed to fetch engagements')
    }
    loading.value = false
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchEngagements()
}

const modalRef = ref<HTMLElement | null>(null)
let bsModal: any | null = null

const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedEngagement = ref<any | null>(null)

onMounted(() => {
    if (modalRef.value) {
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
})

const closeModal = () => {
    if (bsModal) {
        bsModal.hide()
    }
}

const editEngagement = (engagement: any) => {
    openModal(engagement);
}

const saveEngagement = async () => {
    loading.value = true
    try {
        if (form.ID) {
            await master.postRequest("/engagement/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Engagement updated successfully');
                    closeModal();
                    fetchEngagements();
                } else {
                    toast.error('Failed to update engagement');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update engagement');
            })
        } else {
            await master.postRequest("/engagement/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Engagement created successfully');
                    closeModal();
                    fetchEngagements();
                } else {
                    toast.error('Failed to create engagement');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create engagement');
            })
        }
    } catch (error: any) {
        toast.error(error.message || 'Failed to save engagement');
    } finally {
        loading.value = false
    }
}

const deleteEngagement = async (engagement: any) => {
    if (!confirm('Are you sure you want to delete this engagement?')) return

    loading.value = true
    try {
        await master.postRequest("/engagement/delete", {ID: engagement.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Engagement deleted successfully');
                closeModal();
                fetchEngagements();
            } else {
                toast.error('Failed to delete engagement');
            }
        })

    } catch (error: any) {
        toast.error(error.message || 'Failed to delete engagement');
    } finally {
        loading.value = false
    }
}

const addAccount = () => {
    form.informations = form.informations || [];
    form.informations.push({
        ID: null,
        code: '',
        name: '',
    });
};

const removeAccount = (idx: number) => {
    if (form.informations) form.informations.splice(idx, 1);
};

const openModal = (engagement: any | null = null) => {
    if (engagement) {
        modalTitle.value = 'Edit Engagement'
        Object.assign(form, engagement);
        form.informations = engagement.informations ? engagement.informations.map((acc: AccountInformation) => ({ ...acc })) : [];
    } else {
        modalTitle.value = 'Create Engagement'
        Object.assign(form, {...initForm})
        console.log(form)
        console.log(initForm)
    }
    if (bsModal) {
        bsModal.show()
    }
}

const viewEngagement = (engagement: any) => {
    selectedEngagement.value = engagement
    if (!bsViewModal && viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
    if (bsViewModal) {
        bsViewModal.show()
    }
}

const closeViewModal = () => {
    if (bsViewModal) {
        bsViewModal.hide()
    }
    selectedEngagement.value = null
}
</script>